import { LabelChip } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { TrainingScheduleOperationsItem } from "@/api/trainingSchedule";
import {
  extraDatasetCount,
  minFreeGpusLabel,
  minFreeGpusSentence,
  primaryDatasetLabel,
  recurrenceDetail,
  recurrenceLabel,
  scheduleStatusChipColor,
  scheduleStatusLabel,
  scheduleTimeLabel,
  shortSha,
} from "./scheduleFormat";
import styles from "./ScheduleEditDialog.module.css";

/**
 * Schedule 편집 다이얼로그의 **읽기** 영역. 지금 이 Schedule 이 무엇인지 표시한다.
 *
 * 편집 폼과 바뀌는 이유가 다르다. 이 영역은 상류가 보내는 축(recipe 요약·관측값)이 늘거나 줄 때
 * 바뀐다. 폼은 PATCH 로 보낼 수 있는 축이 바뀔 때 바뀐다. 치수는 다이얼로그의 CSS Module 이
 * 소유한다.
 */

// 상태 띠. "지금 어떤 상태이고 다음에는 언제 실행되는가"에 한 줄로 답한다. 상태를 켜고 끄는
// 스위치를 맨 왼쪽(toggle)에 두어 상태와 그 컨트롤이 같은 줄에 있다. 되돌릴 수 없는 삭제는 푸터 왼쪽에 있다.
export function StatusBand({
  item,
  toggle,
}: {
  item: TrainingScheduleOperationsItem;
  // 맨 왼쪽의 활성 스위치. 읽기 전용이면 없다.
  toggle: ReactNode;
}): ReactNode {
  return (
    <div className={styles.statusBand}>
      {toggle}
      <div className={styles.statusIdentity}>
        <LabelChip color={scheduleStatusChipColor(item.status)} size="sm" variant="filled">
          {scheduleStatusLabel(item.status)}
        </LabelChip>
        <span className={styles.statusName} title={item.name}>
          {item.name}
        </span>
      </div>
      <dl className={styles.statusMeta}>
        <InlineFact label="다음 실행" value={scheduleTimeLabel(item.next_run_at)} />
        <InlineFact label="마지막 실행" value={scheduleTimeLabel(item.last_run_at)} />
        <InlineFact label="실행 중인 Session" value={String(item.active_session_count)} />
      </dl>
    </div>
  );
}

function InlineFact({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className={styles.inlineFact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.inlineFactValue}>{value}</dd>
    </div>
  );
}

// 구성. recipe 요약이다. recipe 는 PATCH 로 바꿀 수 없어 편집 단에 자리가 없다. 마지막 줄이 그
// 사실을 안내한다. 안내가 없으면 사용자는 편집 폼에서 recipe 필드를 찾는다. 읽기 전용이면 편집
// 폼이 없으므로 편집 가능한 축(반복·실행 조건)도 여기서 표시한다.
export function ScheduleFacts({
  item,
  showEditableAxes,
}: {
  item: TrainingScheduleOperationsItem;
  showEditableAxes: boolean;
}): ReactNode {
  const recipe = item.schedule_recipe;
  const extraCount = extraDatasetCount(recipe);
  const datasetLabel = `${primaryDatasetLabel(recipe)}${extraCount > 0 ? ` 외 ${extraCount}건` : ""}`;
  return (
    <section className={styles.readSection} aria-label="구성">
      <h3 className={styles.sectionTitle}>구성</h3>
      <dl className={styles.facts}>
        <Fact label="Dataset" value={datasetLabel} />
        <Fact label="Config" value={recipe?.config?.name ?? "—"} />
        <Fact label="learning-core" value={recipe?.learning_core?.ref ?? "—"} />
        {showEditableAxes ? (
          <>
            <Fact label="반복" value={recurrenceLabel(item)} title={recurrenceDetail(item)} />
            <Fact label="종료 시각" value={scheduleTimeLabel(item.end_at)} />
            <Fact
              label="Branch 변경 시만"
              value={item.skip_if_unchanged ? "새 Commit 있을 때만" : "매번"}
            />
            <Fact
              label="여유 GPU 조건"
              value={minFreeGpusLabel(item.min_free_gpus)}
              title={minFreeGpusSentence(item.min_free_gpus)}
            />
            <Fact label="최신 Release 사용" value={item.track_latest_eval_release ? "켬" : "끔"} />
          </>
        ) : null}
      </dl>
      <p className={styles.recipeNotice}>
        Recipe(Dataset·Config)는 여기서 바꿀 수 없습니다. 바꾸려면 새 Schedule을 만들고 기존
        Schedule은 삭제해 주세요.
      </p>
    </section>
  );
}

// 최근 확인 결과. 실행 조건이 남긴 값(확인한 Commit·보류 사유·오류)이다. 값이 하나도 없으면
// 섹션이 없다. 보류 사유와 오류는 "왜 실행되지 않는가"의 답이라 자르지 않고 줄을 바꿔 표시한다.
export function LastObservations({ item }: { item: TrainingScheduleOperationsItem }): ReactNode {
  const hasSha = item.last_resolved_sha != null;
  const hasDefer = item.last_defer_reason != null && item.last_defer_reason !== "";
  const hasError = item.last_error != null && item.last_error !== "";
  if (!hasSha && !hasDefer && !hasError) {
    return null;
  }
  return (
    <section className={styles.readSection} aria-label="최근 확인 결과">
      <h3 className={styles.sectionTitle}>최근 확인 결과</h3>
      <dl className={styles.facts}>
        {hasSha ? (
          <Fact label="마지막 확인 Commit" value={shortSha(item.last_resolved_sha)} mono />
        ) : null}
        {hasDefer ? (
          <Fact
            label="보류 사유"
            value={`${item.last_defer_reason ?? ""} (${scheduleTimeLabel(item.last_deferred_at)})`}
            wrap
          />
        ) : null}
        {hasError ? <Fact label="오류" value={item.last_error ?? ""} wrap mode="danger" /> : null}
      </dl>
    </section>
  );
}

function Fact({
  label,
  value,
  title,
  wrap = false,
  mono = false,
  mode = "primary",
}: {
  label: string;
  value: string;
  // 값 뒤의 원문(cron 원식 등). 있으면 title 로 쓴다.
  title?: string | null;
  // 긴 자유 문장(보류 사유·오류)은 줄을 바꿔 모두 표시한다. 식별자와 이름은 한 줄로 자르고 title 로 원문을 표시한다.
  wrap?: boolean;
  mono?: boolean;
  // 값의 시각 변형(§10). `wrap`·`mono` 는 변형이 아니라 각각 줄바꿈·글꼴 속성이라 따로 둔다.
  // 셋은 독립이고 실제로 함께 쓰인다(오류는 wrap + danger).
  mode?: "primary" | "danger";
}): ReactNode {
  const valueClass = [
    styles.factValue,
    wrap ? styles.factValueWrap : "",
    mono ? styles.monoText : "",
    mode === "danger" ? styles.dangerText : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={valueClass} title={title ?? (wrap ? undefined : value)}>
        {value}
      </dd>
    </div>
  );
}
